/* eslint-disable max-len */
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import color from 'color';

import React, { useState, useEffect } from 'react';
import { ThemeProvider, createGlobalStyle, css } from 'styled-components';

import {
  parse as parseQuery,
  stringify as stringifyQuery,
} from 'querystring';
import { parse as parseUrl } from 'url';

import {
  Redirect,
} from 'react-router-dom';

import DefaultAdmin from 'hive-admin';
import Query from 'hive-admin/src/components/Query';
import {
  Provider as ClipboardProvider,
} from 'hive-admin/src/components/ClipboardContext';

import { Helmet } from 'react-helmet';
import Types from '../modules/types';

// import InternalStoresNavigation from './InternalStoresNavigation';
// import Intercom from './Intercom';
// import ChatGleap from './ChatGleap';

import {
  Provider as SystemCountryProvider,
} from './SystemCountry';

import { FontLoader } from './FontPreview';
import theme from '../theme';

import { logoLoginSrc, logoSidebarSrc, faviconSrc } from './Logo';

// import {
//   PopoverContainer,
//   PopoverContainerRef,
// } from './FieldContentBuilder/Targetable';

const FullWidthFormFieldGlobalStyle = createGlobalStyle`
  #content > * > .ant-form > .ant-row > [data-full-width-form-field="true"] {
    width: calc(100vw - 380px);
    margin-left: min(0px, calc(calc(calc(100vw - 380px) - 820px) / -2));
    [data-expanded-sidebar="false"] & {
      width: calc(100vw - 180px);
      margin-left: min(0px, calc(calc(calc(100vw - 180px) - 820px) / -2));
    }
    [data-is-tablet="true"] & {
      width: calc(100vw - 80px);
      margin-left: min(0px, calc(calc(calc(100vw - 80px) - 820px) / -2));
    }
    [data-is-mobile="true"] & {
      width: calc(100vw - 20px);
      margin-left: min(0px, calc(calc(calc(100vw - 20px) - 820px) / -2));
    }
  }
`;

const getBackgroundPrimary = (storeData, adminData, defaultPrimaryColor) => {
  const storeBackgroundPrimary = storeData?.data?.adminStyle?.colorBackgroundPrimary || null;
  const storePrimary = storeData?.data?.adminStyle?.colorPrimary || null;
  const adminBackgroundPrimary = adminData?.data?.adminStyle?.colorBackgroundPrimary || null;
  const adminPrimary = adminData?.data?.adminStyle?.colorPrimary || null;

  if (storeBackgroundPrimary) {
    return storeBackgroundPrimary;
  }
  if (storePrimary) {
    return color(storePrimary).alpha(0.25).toString();
  }
  if (adminBackgroundPrimary) {
    return adminBackgroundPrimary;
  }
  if (adminPrimary) {
    return color(adminPrimary).alpha(0.25).toString();
  }
  return color(defaultPrimaryColor).alpha(0.25).toString();
};

const getBackgroundSecondary = (storeData, adminData, defaultSecondaryColor, defaultPrimaryColor) => {
  const storeBackgroundSecondary = storeData?.data?.adminStyle?.colorBackgroundSecondary || null;
  const storeBackgroundPrimary = storeData?.data?.adminStyle?.colorBackgroundPrimary || null;
  const storeSecondary = storeData?.data?.adminStyle?.colorSecondary || null;
  const storePrimary = storeData?.data?.adminStyle?.colorPrimary || null;

  const adminBackgroundSecondary = adminData?.data?.adminStyle?.colorBackgroundSecondary || null;
  const adminBackgroundPrimary = adminData?.data?.adminStyle?.colorBackgroundPrimary || null;
  const adminSecondary = adminData?.data?.adminStyle?.colorSecondary || null;
  const adminPrimary = adminData?.data?.adminStyle?.colorPrimary || null;

  if (storeBackgroundSecondary) {
    return storeBackgroundSecondary;
  }
  if (storeBackgroundPrimary) {
    return storeBackgroundPrimary;
  }
  if (storeSecondary) {
    return color(storeSecondary).alpha(0.25).toString();
  }
  if (storePrimary) {
    return color(storePrimary).alpha(0.25).toString();
  }
  if (adminBackgroundSecondary) {
    return adminBackgroundSecondary;
  }
  if (adminBackgroundPrimary) {
    return adminBackgroundPrimary;
  }
  if (adminSecondary) {
    return color(adminSecondary).alpha(0.25).toString();
  }
  if (adminPrimary) {
    return color(adminPrimary).alpha(0.25).toString();
  }
  return color(defaultSecondaryColor).alpha(0.25).toString() || color(defaultPrimaryColor).alpha(0.25).toString();
};

const getColorVariables = ({
    primary,
    secondary,
    button,
    title,
    backgroundPrimary,
    backgroundSecondary,
    logoSidebar,
    logoLogin,
}) => `
  --primary-color: ${primary};
  --secondary-color: ${secondary};
  --color-primary: ${primary};
  --color-secondary: ${secondary};

  --color-title: ${title};

  --color-foreground: ${color(primary).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
  };

  --color-button: ${button};
  --color-button-text: ${color(button).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
  };

  --color-background-primary: ${backgroundPrimary}
  --color-background-secondary: ${backgroundSecondary};
  --color-background: ${backgroundPrimary};
  --color-background-text: ${color(backgroundPrimary).alpha() < 1 || color(backgroundPrimary).luminosity() > 0.6
    ? 'hsla(0, 0%, 0%, 0.9)'
    : '#ffffff'
};

  --antd-color-primary: ${primary};
  --antd-color-secondary: ${secondary};

  --antd-color-title: ${title};

  --antd-color-foreground: ${color(primary).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
  };

  --antd-color-button: ${button};
  --antd-color-button-text: ${color(button).luminosity() > 0.6
     ? '#000000'
     : '#ffffff'
  };

  --antd-color-background-primary: ${backgroundPrimary};
  --antd-color-background-secondary: ${backgroundSecondary};
  --antd-color-background: ${backgroundPrimary};
  --antd-color-background-text: ${color(backgroundPrimary).alpha() < 1 || color(backgroundPrimary).luminosity() > 0.6
    ? 'hsla(0, 0%, 0%, 0.9)'
    : '#ffffff'
  };

  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-lighten-${index * 5}:
      ${color(primary).lighten(0.05 * index).hex()};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-darken-${index * 5}:
      ${color(primary).darken(0.05 * index).hex()};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-fade-${index * 5}:
      ${color(primary).alpha(0.05 * index)};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-tint-${index * 5}:
      ${color(primary).whiten(0.05 * index).hex()};
    `).join('\n')
  };
  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-primary-shade-${index * 5}:
      ${color(primary).blacken(0.05 * index).hex()};
    `).join('\n')
  };

  ${Array.from({ length: 20 }).map((_, index) => `
    --antd-color-secondary-fade-${index * 5}:
      ${color(secondary).alpha(0.05 * index)};
    `).join('\n')
  };

  --antd-color-primary-1: ${color(primary).lighten(0.6).hex()};
  --antd-color-primary-1: ${color(primary).alpha(0.1)};

  --antd-color-primary-2: ${color(primary).lighten(0.5).hex()};
  --antd-color-primary-3: ${color(primary).lighten(0.4).hex()};
  --antd-color-primary-4: ${color(primary).lighten(0.3).hex()};
  --antd-color-primary-5: ${color(primary).lighten(0.2).hex()};
  --antd-color-primary-6: ${color(primary).darken(0.2).hex()};
  --antd-color-primary-7: ${color(primary).darken(0.3).hex()};
  --antd-color-primary-8: ${color(primary).darken(0.4).hex()};
  --antd-color-primary-9: ${color(primary).darken(0.5).hex()};
  --antd-color-primary-10: ${color(primary).darken(0.6).hex()};

  --logo: url(${logoSidebar});
  --logo-sidebar: url(${logoSidebar});
  --logo-login: url(${logoLogin});
`;

const FaviconDelayed = ({ src }) => {
  const [favicon, setFavicon] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFavicon(faviconSrc);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  return (
    favicon ? (
      <Helmet>
        <link rel="icon" href={src} />
      </Helmet>
    ) : null
  );
};

const createCustomGlobalStyle = ({ titleColor, customCss }) => {
  const CustomGlobalStyle = createGlobalStyle`
    ${titleColor && css`
      h1, h1 > span { color: ${titleColor} !important; }
      body { --title-color: ${titleColor}; }
    `}

    ${customCss && css`
      ${customCss}
    `}
  `;

  return <CustomGlobalStyle />;
};

export default class Admin extends DefaultAdmin {
  constructor(props) {
    super(props);
    const { client } = this.props;
    if (!client.systemCountryIntercepted) {
      client.systemCountryIntercepted = true;
      client.interceptors.request.use((config = {}) => {
        const { viewer, searchParams } = this.props;
        const systemCountry = viewer?.systemCountry || searchParams.sc;
        if (!systemCountry) {
          return config;
        }
        const { pathname, query: queryString } = parseUrl(config.url);
        const query = parseQuery(queryString);
        const url = `${pathname}?${stringifyQuery({
          ...(query || {}),
          systemCountry,
        })}`;
        return {
          ...config,
          url,
        };
      });
    }
  }

  renderContent(extraProps = {}) {
    const { page, searchParams } = this.props;
    if (!page) {
      return null;
    }
    const destination = page.getRedirect({ ...this.props, ...page });
    const state = { from: this.props.location };
    if (isObject(destination)) {
      return (
        <Redirect
          key="content"
          to={Object.assign(
            destination,
            { state: Object.assign(state, destination.state || {}) }
          )}
        />
      );
    }
    if (isString(destination)) {
      return (
        <Redirect
          key="content"
          to={{
            pathname: destination,
            search: (
                searchParams['packman-marketplace']
              ? `?${stringifyQuery({
                  'packman-marketplace': searchParams['packman-marketplace'],
                })}`
              : undefined
            ),
            state,
          }}
        />
      );
    }
    const { render, type, getSkip, skip, id, ...restConfig } = page;
    if (this.props?.viewer?.storeData?.onboardingComplete === false) {
      if (![
        '/checkout-card-verification',
        '/onboarding',
      ].includes(id)) {
        return (
          <Redirect
            key="content"
            to="/onboarding"
          />
        )
      }
    }
    return (
      <ClipboardProvider storage={this.props.storage}>
        {
          page.render({
            ...this.props,
            key: page.path,
            ...restConfig,
            ...extraProps,
          })
        }
      </ClipboardProvider>
    );
  }

  renderAfterStyle() {
    const {
      client,
      viewer,
      searchParams,
      location,
      isInitializing,
      isAuthorized,
    } = this.props;
    if (viewer) {
      const lastSystemCountry = (
           Types.SYSTEM_COUNTRY_MAP[client.storage.getItem('systemCountry')]
        || Types.SYSTEM_COUNTRY_LIST[0]
      );
      if (viewer.role === 'ADMIN') {
        if (!searchParams.sc || !Types.SYSTEM_COUNTRY_MAP[searchParams.sc]) {
          return (
            <Redirect
              to={`${location.pathname}?${stringifyQuery({
                ...searchParams,
                sc: lastSystemCountry.id,
              })}`}
            />
          );
        }
      } else if (
        viewer.systemCountry
        && searchParams.sc !== viewer.systemCountry
      ) {
        return (
          <Redirect
            to={`${location.pathname}?${stringifyQuery({
              ...searchParams,
              sc: viewer.systemCountry || undefined,
            })}`}
          />
        );
      }
    } else if (
      !viewer
      && searchParams.sc
      && !isInitializing
      && !isAuthorized
    ) {
      return (
        <Redirect
          to={`${location.pathname}?${stringifyQuery({
            ...searchParams,
            sc: undefined,
          })}`}
        />
      );
    }
    if (searchParams.sc) {
      client.storage.setItem('systemCountry', searchParams.sc);
    }
    const systemCountry = (
      Types.SYSTEM_COUNTRY_MAP[searchParams.sc]
      || Types.SYSTEM_COUNTRY_MAP[Types.SYSTEM_COUNTRY[0]]
    );
    if (client) {
      client.sc = systemCountry;
    }
    return (
      <SystemCountryProvider systemCountry={systemCountry}>
        <FontLoader />
        <FullWidthFormFieldGlobalStyle />
        {super.render()}
        {/* <InternalStoresNavigation
          viewer={viewer}
          searchParams={searchParams}
          client={client}
        /> */}
        {/* {
          viewer?.systemCountry !== 'ae'
          ? <ChatGleap viewer={viewer} />
          : null
        } */}
      </SystemCountryProvider>
    );
  }

  renderWithDefaultStyle({
    colorPrimary,
    colorSecondary,
    colorButton,
    colorTitle,
    logoSidebar,
    logoLogin,
    favicon,
  }) {
    return (
      <>
        <Helmet>
          <meta
            name="theme-color"
            content="#ffffff"
          />
        </Helmet>

        <FaviconDelayed src={faviconSrc} />

        <style>
          {`
            :root {
              ${getColorVariables({
                primary: colorPrimary,
                secondary: colorSecondary || colorPrimary,
                button: colorButton || colorPrimary,
                title: colorTitle,
              })
            }
          `}
        </style>
        <ThemeProvider theme={{
          less: {
            ...theme.less,
            primaryColor: colorPrimary,
            secondaryColor: colorSecondary || colorPrimary,
            buttonColor: colorButton || colorPrimary,
            titleColor: colorTitle,
          },
          logoSidebar,
          logoLogin,
          favicon,
        }}
        >
          {this.renderAfterStyle()}
        </ThemeProvider>
      </>
    );
  }

  render() {
    const isCustomDomain = (
      ['packman.app'].includes(window.location.host)
      ? false
      : true
    );

    const customDomain = isCustomDomain ? window.location.host : null;

    const renderAdmin = ({
      defaultSystemColor = '#000',
      defaultPrimaryColor = '#aa407a',
      defaultSecondaryColor = '#f7a80c',
      defaultTitleColor = '#000',
    } = {}) => (
      <Query
        client={this.props.client}
        url="/stores/packman.app/admin-style"
      >
        {({ data: adminData, loading: adminLoading }) => (
          adminData?.data && !adminLoading
          ? (
          <Query
            client={this.props.client}
            url={`/stores/${customDomain}/admin-style`}
          >
            {({ data: storeData, loading: storeLoading }) => (
              !storeLoading
              ? (
                  <>
                    <style>
                      {`
                        body {
                          ${
                            getColorVariables({
                              primary:
                                storeData?.data?.adminStyle?.colorPrimary
                                || adminData?.data?.adminStyle?.colorPrimary
                                || defaultPrimaryColor
                                || defaultSystemColor,
                              secondary:
                                storeData?.data?.adminStyle?.colorSecondary
                                || storeData?.data?.adminStyle?.colorPrimary
                                || adminData?.data?.adminStyle?.colorSecondary
                                || adminData?.data?.adminStyle?.colorPrimary
                                || defaultSecondaryColor
                                || defaultSystemColor,
                              button:
                                storeData?.data?.adminStyle?.colorButton
                                || adminData?.data?.adminStyle?.colorButton
                                || storeData?.data?.adminStyle?.colorSecondary
                                || storeData?.data?.adminStyle?.colorPrimary
                                || adminData?.data?.adminStyle?.colorSecondary
                                || adminData?.data?.adminStyle?.colorPrimary
                                || defaultSecondaryColor
                                || defaultPrimaryColor
                                || defaultSystemColor,
                              title:
                                storeData?.data?.adminStyle?.colorTitle
                                || adminData?.data?.adminStyle?.colorTitle
                                || defaultTitleColor
                                || defaultSystemColor,
                              backgroundPrimary: getBackgroundPrimary(storeData, adminData, defaultPrimaryColor),
                              backgroundSecondary: getBackgroundSecondary(storeData, adminData, defaultSecondaryColor, defaultPrimaryColor),
                              logoSidebar:
                                storeData?.data?.adminStyle?.logoSidebar?.src
                                || storeData?.data?.adminStyle?.logoLogin?.src
                                || adminData?.data?.adminStyle?.logoSidebar?.src
                                || adminData?.data?.adminStyle?.logoLogin?.src
                                || logoSidebarSrc,
                              logoLogin:
                                storeData?.data?.adminStyle?.logoLogin?.src
                                || storeData?.data?.adminStyle?.logoSidebar?.src
                                || adminData?.data?.adminStyle?.logoLogin?.src
                                || adminData?.data?.adminStyle?.logoSidebar?.src
                                || logoLoginSrc,
                            })
                          }
                        }
                      `}
                      {adminData?.data?.adminStyle?.css || ''}
                    </style>
                    <ThemeProvider theme={{
                      less: {
                        ...theme.less,
                        primaryColor:
                          storeData?.data?.adminStyle?.colorPrimary
                          || adminData?.data?.adminStyle?.colorPrimary
                          || defaultPrimaryColor
                          || defaultSystemColor,
                        secondaryColor:
                          storeData?.data?.adminStyle?.colorSecondary
                          || storeData?.data?.adminStyle?.colorPrimary
                          || adminData?.data?.adminStyle?.colorSecondary
                          || adminData?.data?.adminStyle?.colorPrimary
                          || defaultSecondaryColor
                          || defaultSystemColor,
                        buttonColor:
                          storeData?.data?.adminStyle?.colorButton
                          || adminData?.data?.adminStyle?.colorButton
                          || storeData?.data?.adminStyle?.colorSecondary
                          || storeData?.data?.adminStyle?.colorPrimary
                          || adminData?.data?.adminStyle?.colorSecondary
                          || adminData?.data?.adminStyle?.colorPrimary
                          || defaultSecondaryColor
                          || defaultPrimaryColor
                          || defaultSystemColor,
                        titleColor:
                          storeData?.data?.adminStyle?.colorTitle
                          || adminData?.data?.adminStyle?.colorTitle
                          || defaultTitleColor
                          || defaultSystemColor,
                        backgroundPrimary: getBackgroundPrimary(storeData, adminData, defaultPrimaryColor),
                        backgroundSecondary: getBackgroundSecondary(storeData, adminData, defaultSecondaryColor, defaultPrimaryColor),
                      },
                      logoLogin:
                        storeData?.data?.adminStyle?.logoLogin?.src
                        || storeData?.data?.adminStyle?.logoSidebar?.src
                        || adminData?.data?.adminStyle?.logoLogin?.src
                        || adminData?.data?.adminStyle?.logoSidebar?.src
                        || logoLoginSrc,
                      logoSidebar:
                        storeData?.data?.adminStyle?.logoSidebar?.src
                        || storeData?.data?.adminStyle?.logoLogin?.src
                        || adminData?.data?.adminStyle?.logoSidebar?.src
                        || adminData?.data?.adminStyle?.logoLogin?.src
                        || logoSidebarSrc,
                      favicon:
                        (
                          storeData?.data?.adminStyle?.favicon?.src
                          || storeData?.data?.adminStyle?.logoSidebar
                          || storeData?.data?.adminStyle?.logoLogin
                        )
                        ? (
                          storeData?.data?.favicon?.src
                          || storeData?.data?.adminStyle?.logoSidebar?.src
                          || storeData?.data?.adminStyle?.logoLogin?.src
                        )
                        : (
                          adminData?.data?.adminStyle?.favicon?.src
                          || adminData?.data?.adminStyle?.logoSidebar
                          || adminData?.data?.adminStyle?.logoLogin
                        )
                        ? (
                          adminData?.data?.favicon?.src
                          || adminData?.data?.adminStyle?.logoSidebar?.src
                          || adminData?.data?.adminStyle?.logoLogin?.src
                        )
                        : faviconSrc,
                      // Overrides
                      titleColor: (
                        storeData?.data?.adminStyle?.colorTitle
                        || adminData?.data?.adminStyle?.colorTitle
                        || false
                      ),
                    }}
                    >
                      {this.renderAfterStyle()}
                    </ThemeProvider>
                    <Helmet>
                      <meta
                        name="theme-color"
                        // content={
                        //   storeData?.data?.adminStyle?.colorPrimary
                        //   || adminData?.data?.adminStyle?.colorPrimary
                        //   || defaultPrimaryColor
                        //   || defaultSystemColor
                        // }
                        content="#ffffff"
                      />
                      <link
                        rel="icon"
                        href={
                          (
                            storeData?.data?.adminStyle?.favicon?.src
                            || storeData?.data?.adminStyle?.logoSidebar
                            || storeData?.data?.adminStyle?.logoLogin
                          )
                          ? (
                            storeData?.data?.adminStyle?.favicon?.src
                            || storeData?.data?.adminStyle?.logoSidebar?.src
                            || storeData?.data?.adminStyle?.logoLogin?.src
                          )
                          : (
                            adminData?.data?.adminStyle?.favicon?.src
                            || adminData?.data?.adminStyle?.logoSidebar
                            || adminData?.data?.adminStyle?.logoLogin
                          )
                          ? (
                            adminData?.data?.adminStyle?.favicon?.src
                            || adminData?.data?.adminStyle?.logoSidebar?.src
                            || adminData?.data?.adminStyle?.logoLogin?.src
                          )
                          : faviconSrc
                        }
                      />
                    </Helmet>
                    {createCustomGlobalStyle({
                      titleColor: (
                        storeData?.data?.adminStyle?.colorTitle
                        || adminData?.data?.adminStyle?.colorTitle
                        || defaultTitleColor
                        || defaultSystemColor
                      ),
                      customCss:
                        (
                          storeData?.data?.adminStyle?.customCss
                          || adminData?.data?.adminStyle?.customCss
                          || false
                        ),
                    })}
                  </>
                )
              : storeLoading
                ? this.renderWithDefaultStyle({
                  favicon: '',
                  logoLogin: '',
                  logoSidebar: '',
                  colorPrimary: '#ffffff',
                  colorSecondary: '#ffffff',
                  colorButton: '#000000',
                  colorTitle: '#000000',
                  colorBackgroundPrimary: '#dddddd',
                  colorBackgroundSecondary: '#dddddd',
                })
                : this.renderWithDefaultStyle({
                  favicon: faviconSrc,
                  logoLogin: logoLoginSrc,
                  logoSidebar: logoSidebarSrc,
                  colorPrimary: defaultPrimaryColor || defaultSystemColor,
                  colorSecondary: defaultSecondaryColor || defaultSystemColor,
                  colorButton: defaultPrimaryColor || defaultSystemColor,
                  colorTitle: defaultTitleColor || defaultSystemColor,
                  colorBackgroundPrimary:
                    defaultPrimaryColor || defaultSystemColor,
                  colorBackgroundSecondary:
                    defaultSecondaryColor || defaultSystemColor,
                })
            )}
          </Query>
        )
        : adminLoading
        ? this.renderWithDefaultStyle({
          favicon: '',
          logoLogin: '',
          logoSidebar: '',
          colorPrimary: '#ffffff',
          colorSecondary: '#ffffff',
          colorButton: '#000000',
          colorTitle: '#000000',
          colorBackgroundPrimary: '#dddddd',
          colorBackgroundSecondary: '#dddddd',
        })
        : this.renderWithDefaultStyle({
          favicon: faviconSrc,
          logoLogin: logoLoginSrc,
          logoSidebar: logoSidebarSrc,
          colorPrimary: defaultPrimaryColor || defaultSystemColor,
          colorSecondary: defaultSecondaryColor || defaultSystemColor,
          colorButton: defaultPrimaryColor || defaultSystemColor,
          colorTitle: defaultTitleColor || defaultSystemColor,
          colorBackgroundPrimary: defaultPrimaryColor || defaultSystemColor,
          colorBackgroundSecondary: defaultSecondaryColor || defaultSystemColor,
        })
      )}
      </Query>
    );

    return renderAdmin();
  }
}
